import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import * as LZString from 'lz-string';

export interface Snapshot {
  id?: number;
  layoutId: string;
  timestamp: number;
  snapshot: string; // compressed JSON
  stackIndex?: number; // position in the undo stack at time of save
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDBService extends Dexie {

  snapshots!: Table<Snapshot, number>;

  MAX_SNAPSHOTS = 50;

  constructor() {
    super('KavachLayoutDB');
    this.version(1).stores({
      snapshots: '++id, layoutId, timestamp, [layoutId+timestamp]'
    });
    // Version 2: add stackIndex column for undo/redo position restoration
    this.version(2).stores({
      snapshots: '++id, layoutId, timestamp, stackIndex, [layoutId+timestamp]'
    });
    this.snapshots = this.table('snapshots');
  }

  /**
   * Save a snapshot AND record where in the undo stack this snapshot sits.
   * Keeps only the last MAX_SNAPSHOTS for a given layoutId.
   */
  async saveSnapshot(layoutId: string, snapshot: any, stackIndex?: number): Promise<number> {
    const compressed = LZString.compressToBase64(JSON.stringify(snapshot));
    const id = await this.snapshots.add({ layoutId, timestamp: Date.now(), snapshot: compressed, stackIndex });
    // Prune old snapshots
    const all = await this.snapshots
      .where('[layoutId+timestamp]')
      .between([layoutId, Dexie.minKey], [layoutId, Dexie.maxKey])
      .reverse()
      .toArray();
    if (all.length > this.MAX_SNAPSHOTS) {
      const toDelete = all.slice(this.MAX_SNAPSHOTS);
      await this.snapshots.bulkDelete(toDelete.map(s => s.id!));
    }
    return id;
  }

  /** Get the most-recently saved snapshot for this layout (by timestamp). */
  async getLatestSnapshot(layoutId: string): Promise<Snapshot | undefined> {
    const all = await this.snapshots
      .where('[layoutId+timestamp]')
      .between([layoutId, Dexie.minKey], [layoutId, Dexie.maxKey])
      .reverse()
      .toArray();
    return all[0];
  }

  /**
   * Get all snapshots for a layout ordered newest-first.
   * Useful for restoring the full undo stack on reload.
   */
  async getSnapshots(layoutId: string): Promise<Snapshot[]> {
    return this.snapshots
      .where('[layoutId+timestamp]')
      .between([layoutId, Dexie.minKey], [layoutId, Dexie.maxKey])
      .reverse()
      .toArray();
  }

  async getSnapshot(id: number): Promise<Snapshot | undefined> {
    return this.snapshots.get(id);
  }

  async deleteSnapshot(id: number): Promise<void> {
    await this.snapshots.delete(id);
  }

  async clearSnapshots(layoutId: string): Promise<void> {
    await this.snapshots.where('layoutId').equals(layoutId).delete();
  }

  compressSnapshot(snapshot: any): string {
    return LZString.compressToBase64(JSON.stringify(snapshot));
  }

  decompressSnapshot(data: string): any {
    return JSON.parse(LZString.decompressFromBase64(data)!);
  }
}
